import React, { useState, useEffect } from 'react';
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CheckCircleIcon, DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../context/ToastContext';
import { ConfirmationModal } from '../ui/ConfirmationModal';

export default function AnalysisPreviewModal({ isOpen, onClose, analysisResult, onCommit, onDiscard }) {
    const { addToast } = useToast();
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

    useEffect(() => {
        if (analysisResult) {
            // Auto-select first candidate
            if (analysisResult.candidates && analysisResult.candidates.length > 0) {
                setSelectedProduct(analysisResult.candidates[0]);
            }
            // DEFENSIVE: Do NOT auto-select properties. User must choose.
            setSelectedProperties([]);
        }
    }, [analysisResult]);

    const handlePropertyToggle = (index) => {
        setSelectedProperties(prev =>
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const handleSave = async (mode) => {
        if (!selectedProduct) {
            addToast('Please select a target product first.', 'warning');
            return;
        }

        if (mode === 'data_import' && selectedProperties.length === 0) {
            addToast('Please select at least one property to import.', 'warning');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                stagingId: analysisResult.stagingId,
                targetProductId: selectedProduct.id,
                mode: mode, // 'document_only' or 'data_import'
                approvedProperties: mode === 'data_import'
                    ? analysisResult.extractedData.filter((_, i) => selectedProperties.includes(i))
                    : []
            };

            await onCommit(payload);
            onClose();
        } catch (error) {
            console.error(error);
            addToast('Failed to commit changes.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDiscardClick = () => {
        setShowDiscardConfirm(true);
    };

    const confirmDiscard = async () => {
        setIsSubmitting(true);
        try {
            await onDiscard(analysisResult.stagingId);
            onClose();
        } catch (error) {
            console.error(error);
            addToast('Failed to discard.', 'error');
            setIsSubmitting(false);
        } finally {
            setShowDiscardConfirm(false);
        }
    };

    if (!analysisResult) return null;

    const { classification, candidates, extractedData, warnings } = analysisResult;
    const isDocOnly = classification?.document_intent === 'document_only';

    // Safety check: Logic force DocOnly if intent says so
    const showDataLayer = !isDocOnly && extractedData && extractedData.length > 0;

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-gray-900 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-6xl border border-gray-700">
                                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                                    <button
                                        type="button"
                                        className="rounded-md bg-gray-900 text-gray-400 hover:text-gray-300 focus:outline-none"
                                        onClick={onClose}
                                    >
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>
                                <div className="p-8">
                                    <div className="flex items-start gap-6">
                                        {/* Left Column: Preview Placeholder */}
                                        <div className="w-1/3 bg-gray-800 rounded-lg p-4 h-[650px] flex items-center justify-center border border-gray-700">
                                            <div className="text-center text-gray-500">
                                                <DocumentTextIcon className="h-20 w-20 mx-auto mb-4 opacity-50" />
                                                <p className="text-lg text-gray-300">Document Preview</p>
                                                <p className="text-sm mt-2 text-gray-400 break-all">{analysisResult.tempDetails?.originalName}</p>
                                                <p className="text-xs text-gray-500 mt-1 uppercase">{analysisResult.tempDetails?.mimeType}</p>
                                            </div>
                                        </div>

                                        {/* Right Column: Controls */}
                                        <div className="w-2/3 space-y-6">
                                            <Dialog.Title as="h3" className="text-2xl font-bold leading-6 text-white mb-2">
                                                AI Analysis Proposal
                                            </Dialog.Title>
                                            <p className="text-sm text-gray-400 -mt-4">
                                                Review the AI's suggestions. You must explicitly approve any data changes.
                                            </p>

                                            {/* Classification Alert */}
                                            <div className={`p-4 rounded-lg border flex items-start gap-4 ${isDocOnly ? 'bg-blue-900/20 border-blue-800' : 'bg-yellow-900/10 border-yellow-700/50'}`}>
                                                {isDocOnly ? <CheckCircleIcon className="h-6 w-6 text-blue-400 mt-1" /> : <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 mt-1" />}
                                                <div>
                                                    <h4 className={`font-semibold ${isDocOnly ? 'text-blue-200' : 'text-yellow-200'}`}>
                                                        Classified as: {classification?.document_intent?.replace(/_/g, ' ').toUpperCase()}
                                                    </h4>
                                                    <p className="text-sm text-gray-400 mt-1">
                                                        <span className="font-semibold text-gray-500">Reasoning:</span> {classification?.reason}
                                                    </p>
                                                    <div className="mt-2 flex gap-4 text-xs text-gray-500">
                                                        <span>Confidence: {(classification?.confidence * 100).toFixed(0)}%</span>
                                                        <span>Data Relevance: {(classification?.data_relevance_score * 100).toFixed(0)}%</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Warnings (if any) */}
                                            {warnings && warnings.length > 0 && (
                                                <div className="bg-red-900/20 border border-red-800 p-3 rounded-md">
                                                    <h5 className="text-red-300 text-sm font-semibold">Attention Needed:</h5>
                                                    <ul className="list-disc list-inside text-xs text-red-200 mt-1">
                                                        {warnings.map((w, i) => <li key={i}>{w}</li>)}
                                                    </ul>
                                                </div>
                                            )}

                                            {/* Product Selection */}
                                            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
                                                <label className="block text-sm font-medium text-gray-300 mb-2">Target Product (Required)</label>
                                                <select
                                                    className="w-full bg-gray-900 border border-gray-600 rounded-md py-2 px-3 text-white focus:ring-blue-500 focus:border-blue-500"
                                                    value={selectedProduct?.id || ''}
                                                    onChange={(e) => {
                                                        const prod = candidates.find(c => c.id === parseInt(e.target.value));
                                                        setSelectedProduct(prod);
                                                    }}
                                                >
                                                    <option value="">Select a Product...</option>
                                                    {candidates?.map(p => (
                                                        <option key={p.id} value={p.id}>
                                                            {p.model_name} (SKU: {p.sku})
                                                        </option>
                                                    ))}
                                                </select>
                                                {candidates?.length === 0 && (
                                                    <p className="text-xs text-red-400 mt-2">No product candidates found. Please ensure this document matches a known product.</p>
                                                )}
                                            </div>

                                            {/* Extracted Data Table (Only if relevant) */}
                                            {showDataLayer ? (
                                                <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden flex flex-col h-64">
                                                    <div className="p-3 border-b border-gray-700 flex justify-between items-center bg-gray-800">
                                                        <h4 className="font-semibold text-gray-200 text-sm">Extracted Properties</h4>
                                                        <span className="text-xs text-orange-400 font-medium">Select properties to import</span>
                                                    </div>
                                                    <div className="overflow-y-auto flex-1">
                                                        <table className="min-w-full divide-y divide-gray-700">
                                                            <thead className="bg-gray-800 sticky top-0">
                                                                <tr>
                                                                    <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-10">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
                                                                            onChange={(e) => setSelectedProperties(e.target.checked ? extractedData.map((_, i) => i) : [])}
                                                                            checked={extractedData.length > 0 && selectedProperties.length === extractedData.length}
                                                                        />
                                                                    </th>
                                                                    <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Property</th>
                                                                    <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Value</th>
                                                                    <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Unit</th>
                                                                    <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Target</th>
                                                                    <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Conf.</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-gray-900 divide-y divide-gray-800">
                                                                {extractedData.map((prop, idx) => (
                                                                    <tr key={idx} className={selectedProperties.includes(idx) ? 'bg-blue-900/10' : ''}>
                                                                        <td className="px-6 py-3 whitespace-nowrap">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
                                                                                checked={selectedProperties.includes(idx)}
                                                                                onChange={() => handlePropertyToggle(idx)}
                                                                            />
                                                                        </td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-300 font-medium">{prop.name}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-white">{prop.value}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-400">{prop.unit || '-'}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-500 uppercase">{prop.target_level || '?'}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-500">
                                                                            {(prop.confidence * 100).toFixed(0)}%
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="p-4 rounded-lg border border-gray-700 bg-gray-800/30 text-center">
                                                    <p className="text-gray-500 text-sm">No structured data extracted or document is not relevant for data import.</p>
                                                </div>
                                            )}

                                            <div className="flex gap-4 pt-4 border-t border-gray-700">
                                                <button
                                                    type="button"
                                                    className="rounded-md px-3.5 py-2.5 text-sm font-semibold text-gray-400 hover:text-white hover:bg-red-900/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                                                    onClick={handleDiscardClick}
                                                    disabled={isSubmitting}
                                                >
                                                    Discard
                                                </button>
                                                <div className="flex-1"></div>

                                                {/* Secondary Action: Save Doc Only */}
                                                <button
                                                    type="button"
                                                    className="rounded-md bg-gray-700 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    onClick={() => handleSave('document_only')}
                                                    disabled={isSubmitting || !selectedProduct}
                                                >
                                                    {isSubmitting ? 'Saving...' : 'Save Document Only'}
                                                </button>

                                                {/* Primary Action: Import Data (Only if visible) */}
                                                {showDataLayer && (
                                                    <button
                                                        type="button"
                                                        className="rounded-md bg-orange-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        onClick={() => handleSave('data_import')}
                                                        disabled={isSubmitting || !selectedProduct || selectedProperties.length === 0}
                                                    >
                                                        {isSubmitting ? 'Saving...' : 'Apply Data to Database'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
            <ConfirmationModal
                isOpen={showDiscardConfirm}
                onClose={() => setShowDiscardConfirm(false)}
                onConfirm={confirmDiscard}
                title="Discard Analysis"
                message="Are you sure you want to discard this analysis? This cannot be undone."
                confirmText="Discard"
                isDestructive={true}
            />
        </Transition.Root>
    );
}
